<template>
  <div>
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
      <div class="flex items-center gap-2 my-4 md:my-8">
        <span
          class="filter-btn"
          :class="canEditSelection ? 'cursor-pointer' : 'cursor-default'"
          @click="toggleUpdateModal"
        >
          <EditIcon width="20" color="#68688D" />
          Editar
          <v-tooltip v-if="!canEditSelection" activator="parent" location="bottom">
            {{ editDisabledReason }}
          </v-tooltip>
        </span>

        <span
          @click="toggleRemoveModal"
          class="filter-btn"
          :class="hasSelection ? 'cursor-pointer' : 'cursor-default'"
        >
          <DeleteIcon width="20" color="#68688D" />Excluir
          <v-tooltip v-if="!hasSelection" activator="parent" location="bottom">
            Para remover selecione pelo menos um plantão
          </v-tooltip>
        </span>

        <span
          @click="toggleFilterModal"
          class="filter-btn"
          :class="hasFilter ? 'border border-primary text-primary' : ''"
        >
          <v-icon
            size="small"
            :icon="mdiFilterOutline"
            :color="hasFilter ? 'primary' : 'iconGrey'"
          />
          Filtros
        </span>
      </div>

      <div
        v-if="consulting.totalRecords > 0"
        class="flex flex-col md:flex-row items-center gap-4 mt-4 md:mt-0"
      >
        <div>
          <div
            class="px-3 py-1 bg-white border border-gray-200 rounded-md text-sm font-medium text-gray-700 shadow-sm"
          >
            {{ isGlobalSelectAll ? consulting.totalRecords : selected.length }} plantões
            selecionados
          </div>
        </div>

        <div>
          <v-checkbox
            v-model="isGlobalSelectAll"
            :label="`${isGlobalSelectAll ? 'Desselecionar' : 'Selecionar'} todos plantões`"
            density="compact"
            hide-details
          />
        </div>
      </div>
    </div>

    <div class="mt-4 flex flex-col gap-4">
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
        <filters-used />

        <div v-if="hasFilter" class="flex">
          <button
            @click="clearAllFilters"
            class="text-sm font-bold text-primary hover:text-primary-darken-1 transition-colors"
          >
            Limpar filtros
          </button>
        </div>
      </div>

      <template v-if="isLoading">
        <CheckShiftSkeleton />
      </template>

      <!-- Falha no load ≠ "Nenhum plantão encontrado": estado de erro com retry
           (mesmo padrão ReportErrorState do piloto M1). -->
      <template v-else-if="consultingLoadError">
        <ReportErrorState
          :title="CONSULTING_LOAD_ERROR"
          :message="consultingLoadErrorDetail"
          @retry="loadItems"
        />
      </template>

      <template v-else-if="consulting.totalRecords > 0">
        <v-data-table
          v-model="selected"
          class="overflow-hidden text-left text-sm"
          :hover="true"
          :headers="headers"
          :items="consulting.rows"
          item-value="id"
          :items-per-page="itemsPerPage"
          @update:sort-by="changeSort"
          @update:page="changePage"
          show-select
          no-data-text="Nenhum dado encontrado"
        >
          <template #[`item.name`]="{ item }">
            <span
              v-if="isUnidentifiedCoverage(item.raw)"
              class="italic text-gray-500"
              data-testid="consult-unidentified-name"
              >{{ UNIDENTIFIED_NAME_LABEL }}</span
            >
            <span v-else>{{ item.raw.name }}</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              :data-testid="`check-attempts-button-${item.raw.id}`"
              @click.stop="openCheckAttempts(item.raw)"
            >
              <v-icon :icon="mdiMapMarkerRadiusOutline" size="20" />
              <v-tooltip activator="parent" location="bottom">Tentativas de check</v-tooltip>
            </v-btn>
          </template>

          <template v-slot:bottom>
            <div class="flex justify-center p-4">
              <v-pagination
                v-model="page"
                :total-visible="3"
                :length="pageCount"
                density="comfortable"
                color="primary"
                @update:model-value="changePage"
              />
            </div>
          </template>
        </v-data-table>
      </template>

      <div
        v-else
        data-testid="consult-empty-state"
        class="flex flex-col items-center justify-center p-6 md:p-12 rounded-lg text-center"
      >
        <v-icon class="mb-4 text-gray-500" size="large" :icon="mdiOfficeBuildingPlusOutline" />
        <h3 class="text-xl font-semibold text-gray-700 mb-2">{{ emptyStateTitle }}</h3>
        <p class="text-sm text-gray-500 mb-4">
          {{ emptyStateMessage }}
        </p>
        <v-btn
          class="ma-2 font-semibold"
          color="primary"
          data-testid="consult-open-filters-button"
          @click="toggleFilterModal"
        >
          Abrir Filtros
        </v-btn>
      </div>
    </div>
  </div>

  <UpdateBulkStationModal
    :ids="selected"
    :isSelectAll="isGlobalSelectAll"
    :totalRows="consulting.totalRecords"
    :filters="filters"
    :rows="consulting.rows"
    @refresh-data="loadItems"
  />
  <RemoveBulkStationModal
    :ids="selected"
    :is-select-all="isGlobalSelectAll"
    :filters="filters"
    :total-items-to-delete="isGlobalSelectAll ? consulting.totalRecords : selected.length"
    @refresh-data="loadItems"
  />
  <CheckAttemptsModal />

  <teleport to="#app">
    <FilterStationModal
      :show-sector-specialty="isDashboardV2"
      show-coverage-status
      @apply-filters="handleApplyFilters"
    />
  </teleport>
</template>

<script setup lang="ts">
import { mdiOfficeBuildingPlusOutline, mdiFilterOutline, mdiMapMarkerRadiusOutline } from '@mdi/js'
import EditIcon from '@/assets/icons/edit.vue'
import DeleteIcon from '@/assets/icons/delete.vue'
import CheckShiftSkeleton from '@/components/designSystem/Skeleton/CheckShiftSkeleton.vue'
import { ref, onMounted, watch, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useStationStore, CONSULTING_LOAD_ERROR } from '@/pinia-store/useStationStore'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import { ConsultingRequest } from '@/Network/Types/Requests/StationRequests'
import type { RowConsultingResponse } from '@/Network/Types/Responses/StationResponses'
import UpdateBulkStationModal from './modals/update.bulk.station.modal.vue'
import RemoveBulkStationModal from './modals/remove.bulk.station.modal.vue'
import CheckAttemptsModal from './modals/check.attempts.modal.vue'
import FilterStationModal from '@/components/Filters/filter.stations.vue'
import { useModalStore, EModals } from '@/pinia-store/useModalStore'
import { computed } from 'vue'
import { VDataTable } from 'vuetify/lib/labs/components.mjs'
import { useFilterStationsStore } from '@/components/Filters/useFilterStation'
import { useRoute } from 'vue-router'
import {
  UNIDENTIFIED_NAME_LABEL,
  consultValueCell,
  withCoverageHeaders
} from './consultCoverageColumns'
import { isUnidentifiedCoverage } from '@/utils/coverage'
import FiltersUsed from './filters.used.vue'
import { rowSector, rowSpecialty } from './modals/useStation'
import { catalogDisplayName } from '@/utils/stationDisplay'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

// A11: colunas e filtros de setor/especialidade só com a flag ligada.
const isDashboardV2 = useFeatureFlags().flags.enabledDashboardV2
const SECTOR_SPECIALTY_HEADER_KEYS = ['sectorName', 'specialtyName']

const isMobile = ref(window.innerWidth < 768)
const handleResize = () => {
  isMobile.value = window.innerWidth < 768
}
window.addEventListener('resize', handleResize)

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})

const route = useRoute()
const useFilterStations = useFilterStationsStore()
const { filters } = storeToRefs(useFilterStations)

const hasFilter = computed(() => {
  return Object.values(filters.value).some((f) => f)
})

// type Headers = InstanceType<typeof VDataTable>['headers']

// A aba precisa de periodo: sem `startDate`/`endDate` a API responde
// "startDate and endDate are required for consulting" e o erro cru vazava para o
// ReportErrorState. Sem periodo nao ha consulta — so o convite a filtrar.
const hasPeriod = computed(() => Boolean(filters.value.startDate && filters.value.endDate))

const emptyStateTitle = computed(() =>
  hasPeriod.value
    ? 'Nenhum plantão encontrado'
    : 'Selecione um período nos filtros para consultar os plantões'
)

const emptyStateMessage = computed(() =>
  hasPeriod.value
    ? 'Você pode utilizar os filtros para refinar sua busca.'
    : 'A consulta precisa de uma data inicial e uma data final.'
)

onMounted(async () => {
  // Deep link (/consultar?company=…&startDate=…): restaura os filtros da URL
  // antes do primeiro load para consultar com eles, nao sem periodo.
  useFilterStations.setFiltersFromQuery(route?.query)
  await loadItems()
})

const allHeaders: any[] = [
  {
    key: 'id',
    align: 'start',
    title: 'Id'
  },
  {
    key: 'name',
    align: 'start',
    title: 'Nome'
  },
  {
    key: 'date',
    align: 'start',
    title: 'Data'
  },
  {
    key: 'hours',
    align: 'start',
    title: 'Horário'
  },
  {
    key: 'value',
    align: 'start',
    title: 'Valor',
    sortable: false,
    value: (row: RowConsultingResponse) => consultValueCell(row)
  },
  {
    key: 'schedule',
    align: 'start',
    title: 'Escala'
  },
  {
    key: 'sectorName',
    align: 'start',
    title: 'Setor',
    sortable: false,
    value: (row: RowConsultingResponse) => catalogDisplayName(rowSector(row), '—')
  },
  {
    key: 'specialtyName',
    align: 'start',
    title: 'Especialidade',
    sortable: false,
    value: (row: RowConsultingResponse) => catalogDisplayName(rowSpecialty(row), '—')
  },
  {
    key: 'timezone',
    align: 'start',
    title: 'Fuso horário',
    sortable: false
  },
  {
    key: 'startHour',
    align: 'start',
    title: 'Início'
  },
  {
    key: 'endHour',
    align: 'start',
    title: 'Fim'
  },
  {
    key: 'ourTeam',
    align: 'start',
    title: 'Nossa equipe'
  },
  {
    key: 'stationType',
    align: 'start',
    title: 'Tipo',
    sortable: false
  },
  {
    key: 'inCash',
    align: 'start',
    title: 'Á vista',
    sortable: false
  },
  {
    key: 'actions',
    align: 'end',
    title: '',
    sortable: false
  }
]

const mobileHeaderKeys = ['name', 'date', 'hours', 'value', 'schedule', 'actions']

const visibleHeaders = isDashboardV2
  ? allHeaders
  : allHeaders.filter((h) => !SECTOR_SPECIALTY_HEADER_KEYS.includes(h.key))

const headers = computed(() => {
  if (isMobile.value) {
    return visibleHeaders.filter((h) => mobileHeaderKeys.includes(h.key))
  }
  return withCoverageHeaders(visibleHeaders, consulting.value.rows)
})

const itemsPerPage = 20
const page = ref(1)
const pageCount = computed(() => {
  return Math.ceil(consulting.value.totalRecords / itemsPerPage)
})

const changePage = async (p: number) => {
  serverParams.value = Object.assign({}, serverParams.value, { page: p })
  await loadItems()
}

const isLoading = ref(false)
const isGlobalSelectAll = ref(false)

const serverParams = ref<ConsultingRequest>({
  columnFilters: {},
  sort: [],
  page: 1,
  perPage: 10
})

const modalStore = useModalStore()
const selected = ref<number[]>([])

const stationStore = useStationStore()
const { consulting, consultingLoadError } = storeToRefs(stationStore)

// Evita repetir o fallback genérico como detalhe sob o título fixo (mesmo
// padrão loadErrorDetail do Dashboard/Index.vue).
const consultingLoadErrorDetail = computed(() =>
  consultingLoadError.value && consultingLoadError.value !== CONSULTING_LOAD_ERROR
    ? consultingLoadError.value
    : null
)

const hasSelection = computed(() => Boolean(selected.value.length || isGlobalSelectAll.value))

// A edição em massa é por empresa: sem empresa no filtro o modal abriria sem ter
// o que editar (e sem seleção, anunciando "Editando 0 plantões").
const canEditSelection = computed(() => hasSelection.value && Boolean(filters.value.company))

const editDisabledReason = computed(() =>
  hasSelection.value
    ? 'Para editar selecione uma empresa nos filtros'
    : 'Para editar selecione pelo menos um plantão'
)

const toggleUpdateModal = () => {
  if (!canEditSelection.value) return
  modalStore.toggleModal(EModals.UPDATE_BULK_STATION)
}
const toggleRemoveModal = () => {
  if (!hasSelection.value) return
  modalStore.toggleModal(EModals.REMOVE_BULK_STATION)
}

const toggleFilterModal = () => {
  modalStore.toggleModal(EModals.FILTER_STATION)
}

function openCheckAttempts(item: RowConsultingResponse) {
  modalStore.toggleModal(EModals.CHECK_ATTEMPTS, {
    stationId: item.id,
    doctorName: item.name,
    date: item.date,
    hours: item.hours,
    schedule: item.schedule
  })
}

// Colunas sintéticas da tabela que não existem na entidade DoctorStation do backend.
// "hours" (início–fim) é ordenada pelo campo real startHour; as demais não são ordenáveis.
const SORT_FIELD_ALIASES: Record<string, string> = {
  hours: 'startHour'
}

const changeSort = async (sort: { key: string; order: string }[]) => {
  serverParams.value = Object.assign({}, serverParams.value, {
    sort: sort.map((s) => ({ field: SORT_FIELD_ALIASES[s.key] ?? s.key, type: s.order }))
  })
  await loadItems()
}

async function loadItems() {
  selected.value = []
  isGlobalSelectAll.value = false

  if (!hasPeriod.value) {
    stationStore.clearConsulting()
    isLoading.value = false
    return
  }

  isLoading.value = true
  serverParams.value.columnFilters = { ...filters.value }
  await stationStore.getListConsultingStations(serverParams.value).finally(() => {
    isLoading.value = false
    selected.value = []
    isGlobalSelectAll.value = false
  })
}

async function clearAllFilters() {
  useFilterStations.$reset()
  await loadItems()
}

function updateParams(newProps) {
  serverParams.value = Object.assign({}, serverParams.value, newProps)
}

async function handleApplyFilters(filters) {
  updateParams({ columnFilters: filters })
  await loadItems()
}

watch(isGlobalSelectAll, (newVal) => {
  if (newVal) {
    selected.value = consulting.value.rows.map((item) => item.id)
  } else {
    selected.value = []
  }
})

watch(
  () => consulting.value.rows,
  (newRows) => {
    if (isGlobalSelectAll.value) {
      selected.value = newRows.map((item) => item.id)
    }
  },
  { deep: true }
)
</script>
